import { StreamingTranscriber } from "assemblyai/streaming";
import { STREAMING_SAMPLE_RATE, STREAMING_URL } from "./universalStreamingConfig";
import { isFormattedFinalTurn, isSuspiciousTranscriptShrink } from "./turnLifecycle";
import { getTokenFetchRetryDelayMs } from "@/lib/voiceConnectionPolicy";
import { postAssemblyClientLog } from "./log";

export const MAX_RECONNECT_ATTEMPTS = 20;
export const FINALIZE_TIMEOUT_MS = 5000;
const MAX_BUFFER_MS = 20_000;
const ACTIVITY_EVENTS = ["speechStarted", "warning", "speakerRevision", "llmGatewayResponse", "heartbeat", "silence"];
const UPDATE_FIELDS = {
  minTurnSilence: "min_turn_silence", maxTurnSilence: "max_turn_silence",
  prompt: "prompt", keytermsPrompt: "keyterms_prompt", languageCodes: "language_codes",
};

function scheduleTimeout(callback, delay) {
  const timer = setTimeout(callback, delay);
  return () => clearTimeout(timer);
}

/**
 * One SDK transport, bounded audio buffer, turn store and retry policy.
 * Guide supplies an audio-clock scheduler: no chained main-thread timers.
 * Token expiry is a redemption deadline, not an active-session lifetime.
 * getToken(signal) returns a token or { token, config }; every retry calls it.
 */
export function createStreamingSession({
  getToken, config: initialConfig = {}, onState, onTurn, onError, onActivity, onAudioSent,
  scheduleRetry = scheduleTimeout,
  log = postAssemblyClientLog,
}) {
  let config = { ...initialConfig };
  let status = "idle";
  let error = null;
  let current = null;
  let generation = 0;
  let failures = 0;
  let cancelRetry = null;
  let stopping = false;
  let finished = false;
  let finalizing = null;
  let ready = null;
  let resolveReady;
  let rejectReady;
  let providerConfiguration = null;
  let queuedBytes = 0;
  let droppedAudioMs = 0;
  const queue = [];
  const turns = new Map();
  const ignoredTurns = new Set();

  function snapshot() {
    const ordered = [...turns.values()];
    const lastTurn = ordered.at(-1) || null;
    return {
      status, error, turns: ordered, lastTurn, providerConfiguration,
      transcript: ordered.map(turn => turn.transcript).filter(Boolean).join(" "),
      finalTranscript: ordered.filter(turn => turn.final).map(turn => turn.transcript).filter(Boolean).join(" "),
      partialTranscript: lastTurn && !lastTurn.final ? lastTurn.transcript : "",
      clientSessionId: generation,
    };
  }

  function publish(next = status) {
    status = next;
    onState?.(snapshot());
  }

  function report(cause) {
    error = cause instanceof Error ? cause : new Error(String(cause));
    onError?.(error);
    log({ event: "streaming_error", details: { code: error.code, message: error.message, generation, failures } });
  }

  function active(socket) {
    return !finished && current === socket;
  }

  function handleTurn(socket, message) {
    if (!active(socket)) return;
    onActivity?.({ generation: socket.generation });
    const key = `${socket.generation}:${message.turn_order}`;
    if (ignoredTurns.has(key)) return;
    const previous = turns.get(key);
    const final = isFormattedFinalTurn(message);
    if (previous?.final && !final) return;
    let transcript = typeof message.transcript === "string" ? message.transcript.trim() : "";
    if (previous && isSuspiciousTranscriptShrink(previous.transcript, transcript)) transcript = previous.transcript;
    const turn = { ...message, transcript, key, final, clientSession: socket.generation };
    turns.set(key, turn);
    onTurn?.(turn);
    publish();
  }

  function closeSocket(socket, wait = false) {
    socket?.controller.abort();
    return socket?.transcriber?.close(wait, FINALIZE_TIMEOUT_MS).catch(() => {}) || Promise.resolve();
  }

  function lost(cause) {
    cancelRetry?.();
    cancelRetry = null;
    const socket = current;
    current = null;
    void closeSocket(socket);
    // Audio held for an abandoned connection must not reach the next socket.
    droppedAudioMs += queuedBytes / (STREAMING_SAMPLE_RATE * 2) * 1000;
    queue.length = 0;
    queuedBytes = 0;
    report(cause);
    publish("lost");
    rejectReady?.(error);
  }

  function retry(socket, cause, failureDurationMs) {
    if (!active(socket) || stopping) return;
    current = null;
    void closeSocket(socket);
    failures += 1;
    // cause.status is an HTTP status from the token fetch; cause.code is a WebSocket close code.
    const permanent = [401, 403].includes(cause.status) || (cause.retryable === false && cause.code !== 1008);
    if (permanent || failures >= MAX_RECONNECT_ATTEMPTS) { lost(cause); return; }
    report(cause);
    publish("reconnecting");
    const delay = getTokenFetchRetryDelayMs({ attempt: failures, failureDurationMs });
    cancelRetry = scheduleRetry(() => { cancelRetry = null; void open(); }, delay);
  }

  function send(socket, chunk) {
    socket.transcriber.sendAudio(chunk.pcm);
    onAudioSent?.({ generation: socket.generation, metadata: chunk.metadata, bytes: chunk.pcm.byteLength });
  }

  function drain(socket) {
    while (queue.length && active(socket)) {
      send(socket, queue[0]);
      queuedBytes -= queue.shift().pcm.byteLength;
    }
  }

  async function open() {
    if (stopping || finished || current) return;
    const socket = { generation: ++generation, controller: new AbortController(), transcriber: null, opened: false };
    current = socket;
    const startedAt = Date.now();
    try {
      const credential = await getToken(socket.controller.signal);
      if (!active(socket) || stopping) return;
      const token = typeof credential === "string" ? credential : credential.token;
      config = { ...config, ...(credential.config || {}) };
      const transcriber = new StreamingTranscriber({
        ...config, token, sampleRate: STREAMING_SAMPLE_RATE, websocketBaseUrl: STREAMING_URL,
        formatTurns: true, maxConnectionRetries: 0, connectTimeout: 10_000,
      });
      socket.transcriber = transcriber;
      transcriber.on("turn", message => handleTurn(socket, message));
      ACTIVITY_EVENTS.forEach(name => transcriber.on(name, () => {
        if (active(socket)) onActivity?.({ generation: socket.generation });
      }));
      transcriber.on("close", (code, reason) => {
        const cause = Object.assign(new Error(`Voice connection closed (${code}${reason ? `: ${reason}` : ""})`), { code });
        retry(socket, cause);
      });
      transcriber.on("error", cause => retry(socket, cause));
      const begin = await transcriber.connect();
      if (!active(socket) || stopping) { await closeSocket(socket); return; }
      socket.opened = true;
      providerConfiguration = begin?.configuration || null;
      failures = 0;
      error = null;
      drain(socket);
      publish("live");
      resolveReady?.();
    } catch (cause) {
      retry(socket, cause, Date.now() - startedAt);
    }
  }

  function start() {
    if (finished || stopping) return Promise.reject(new Error("A streaming session starts once."));
    if (!ready) {
      ready = new Promise((resolve, reject) => { resolveReady = resolve; rejectReady = reject; });
      // A callback-only consumer such as Guide may observe status instead.
      ready.catch(() => {});
      publish("connecting");
      void open();
    }
    return ready;
  }

  /** True means sent or retained in the bounded buffer, never silently dropped. */
  function sendAudio(pcm, metadata) {
    if (stopping || finished || status === "lost" || !ready) return false;
    if (!(pcm instanceof ArrayBuffer) && !ArrayBuffer.isView(pcm)) return false;
    if (!pcm.byteLength) return false;
    const chunk = { pcm, metadata };
    if (current?.opened) {
      try { send(current, chunk); return true; }
      catch (cause) { retry(current, cause); }
      // Falling through to the queue is intentional: the chunk is retransmitted when the socket reopens.
    }
    if (status === "lost") return false;
    if (queuedBytes + pcm.byteLength > STREAMING_SAMPLE_RATE * 2 * MAX_BUFFER_MS / 1000) {
      lost(new Error("Voice connection was interrupted too long. Stop recording and reconnect."));
      return false;
    }
    queue.push(chunk);
    queuedBytes += pcm.byteLength;
    return true;
  }

  function updateConfiguration(next) {
    config = { ...config, ...next };
    if (!current?.opened || stopping) return false;
    const updates = Object.fromEntries(Object.entries(UPDATE_FIELDS)
      .filter(([name]) => name in next).map(([name, key]) => [key, next[name]]));
    try { current.transcriber.updateConfiguration(updates); return true; }
    catch (cause) { retry(current, cause); return false; }
  }

  function forceEndpoint() {
    if (!current?.opened || stopping) return false;
    try { current.transcriber.forceEndpoint(); return true; }
    catch (cause) { retry(current, cause); return false; }
  }

  function reset() {
    for (const key of turns.keys()) ignoredTurns.add(key);
    turns.clear();
    publish();
  }

  function reconnect() {
    if (stopping || finished || status === "live" || current) return;
    cancelRetry?.();
    cancelRetry = null;
    failures = 0;
    publish("reconnecting");
    void open();
  }

  /** Unexpected runtime stalls (Guide's watchdog) use the same retry path. */
  function interrupt(cause) {
    if (current) retry(current, cause);
  }

  async function finish(wait) {
    stopping = true;
    cancelRetry?.();
    cancelRetry = null;
    const socket = current;
    const pendingAudioMs = queuedBytes / (STREAMING_SAMPLE_RATE * 2) * 1000 + droppedAudioMs;
    if (wait && socket?.opened) {
      try { socket.transcriber.forceEndpoint(); } catch (cause) { report(cause); }
      await closeSocket(socket, true);
    } else {
      await closeSocket(socket);
    }
    current = null;
    finished = true;
    rejectReady?.(new DOMException("Voice session stopped", "AbortError"));
    const result = snapshot();
    queue.length = 0;
    queuedBytes = 0;
    publish("stopped");
    return {
      ...result, pendingAudioMs,
      status: pendingAudioMs || result.turns.some(turn => !turn.final) ? "partial" : "finalized",
    };
  }

  function finalize() {
    if (!finalizing) finalizing = finish(true);
    return finalizing;
  }

  function stop() {
    if (!finalizing) finalizing = finish(false);
    return finalizing;
  }

  return { start, stop, finalize, sendAudio, forceEndpoint, updateConfiguration, reset, reconnect, interrupt, getSnapshot: snapshot };
}
